/**
 * Shared shapes for the outline row kit. The row interface is deliberately structural — the live
 * Service Plan row and the template row satisfy it without either model importing the other.
 */
import { InjectionToken } from '@angular/core';
import { ActivityEntityName, DayShortName, Entity, EntityLink, PlannedTaskScheduleExceptions, ServiceLineDefaultWindow, ServicePlanBlackoutRange } from '@jms/model';
import { BadgeMode } from '@workbuddy/ui/components/badge';
import { ScheduleRule } from '@jms/model/scheduling/schedule.rule';

/**
 * The row fields the kit reads, and no more. `PlannedTaskOutlineRow` — the one projection row every
 * planned-work outline is now drawn from — is assignable to this, and it is deliberately NOT imported
 * here: the kit reads a structural subset, so it stays uncoupled from the projection's own interface
 * and a column added there for one parent cannot reach the kit until the kit asks for it.
 *
 * The owning-branch reference is absent from this interface on purpose. The live projection calls it
 * `serviceLine` and the template projection `templateLine`, so the kit reaches it through
 * {@link OutlineEntityKinds.lineRef} instead of committing to either name.
 *
 * The schedule anchors are optional because the template side has none — a template has no calendar
 * to anchor to. Absent anchors degrade the schedule card; they never blank the chip.
 */
export interface OutlineRow {
  _id: string;
  rowId: string;
  parentId?: string;
  hasChildren: boolean;
  sourceEntity: ActivityEntityName;
  sourceId: string;
  name: string;
  sortOrder: string;
  description?: string;
  workCategory?: Entity;
  defaultResources?: Entity[];
  mandatory?: boolean;
  schedule?: ScheduleRule;
  scheduleExceptions?: PlannedTaskScheduleExceptions;
  /** Live only. A template row has no cadence anchor, so the chip's start clause never fires there. */
  startsOn?: string;
  /** Live only. Feeds the afterService next due. */
  lastCompleted?: string;
  timezone?: string;
  /** Live only. Line start date. */
  effectiveFrom?: string;
  workingDays?: DayShortName[];
  /** Live only. Line blackouts. */
  blackoutRanges?: ServicePlanBlackoutRange[];
  duration?: number;
  window?: ServiceLineDefaultWindow;
  items?: number;
  evidence?: string[];
  /**
   * Trailing state chips, already resolved to label and icon by whoever built the row.
   *
   * The renderer paints these and asks nothing about what they mean. That is the point: a Visit row
   * needs to say "Excluded" or "Added for this visit", and the alternative — teaching the shared
   * renderer to read `visitChange` — would put one outline's vocabulary into the component all three
   * outlines draw. Ordering is the projection's, so the chips read the same way on every row.
   *
   * `mode` is the only presentation the row may name, and it is optional: the renderer's chip lane
   * owns colour, size and cap so a badge cannot break the row's fixed trailing slot.
   */
  badges?: OutlineRowBadge[];
}

/** One resolved trailing chip. `mode` defaults to the pill the property chips already use. */
export interface OutlineRowBadge {
  id: string;
  label: string;
  mode?: BadgeMode;
  tooltip?: string;
  iconLeft?: string;
}

/**
 * Everything the outline kit needs to know about the entity pair it is rendering. The live Service
 * Plan outline and the Service Plan Template outline mount the same components with different values
 * of this.
 */
export interface OutlineEntityKinds {
  /** The parent/branch entity — a Service Line, or its template twin. */
  line: ActivityEntityName;
  /** The leaf entity — a Service Line Task, or its template twin. */
  task: ActivityEntityName;
  /** Semantic parent type persisted when a task takes a subtask; may differ from the unified task entity. */
  taskParent?: ActivityEntityName;
  /** The read projection the outline board queries. */
  outline: ActivityEntityName;
  linePrefix: string;
  taskPrefix: string;
  /**
   * Reads the row's owning branch reference. The two projections name that field differently
   * (`serviceLine` vs `templateLine`), and an accessor keeps that difference in the one place that
   * knows it. Normalising the field on the way into the grid would instead copy the whole tree on
   * every load and break the row identity AG Grid compares against.
   */
  lineRef: (row: Partial<OutlineRow>) => Entity | undefined;
  /**
   * The create-dialog param the task entity's Form reads its owning line from (`serviceLine` for the
   * live pair, `templateLine` for templates). The form surfaces own their param names; the kit only
   * has to address the right one when it opens a child create.
   */
  lineParam: string;
  /**
   * Whether this outline sits on a calendar. The live plan does, so its schedule card dates the next
   * visit. A template does not — it defines a cadence to be applied later, with no start to count
   * from — so its card stops at the rule rather than inventing a date. Defaults to true.
   *
   * This softens the CARD; it does not remove the chip. A template still shows its cadence, which is
   * the whole point of authoring one. To drop the chip entirely, see {@link hasSchedule}.
   */
  datesVisits?: boolean;
  /**
   * Whether rows on this outline carry a cadence AT ALL. Defaults to true.
   *
   * Distinct from {@link datesVisits}, which only says the outline cannot date the next visit. A
   * template has no calendar but genuinely carries a cadence, so it keeps its chip. A VISIT carries
   * none: it is one attendance, so there is no rhythm to show and a chip would invite editing the
   * recurring plan's schedule from a single occurrence. That is a statement about the outline, not
   * about a view — which is why the renderer honours it even when the server view lists the field.
   */
  hasSchedule?: boolean;
  /**
   * Extra params the child-create dialog needs beyond the owning line.
   *
   * Scope, not decoration. A Visit's create must carry `visitId`, because the same task editor saves
   * an override for one attendance when it has one and edits the recurring task when it does not —
   * so omitting this would silently write a child into the plan from a Visit's own `+` button. The
   * plan and template outlines need nothing here: their line reference already IS the scope.
   */
  createParams?: Record<string, unknown>;
}

/**
 * Per-mount entity pair for the outline kit.
 *
 * Provided at the ROOT injector, because AG Grid builds cell renderers from the grid's own
 * ViewContainerRef — the relation builder's injector is not on that chain, so a per-mount provider
 * cannot reach the renderer through DI. The per-mount value therefore travels on the board's
 * `params` bag (the sanctioned mount channel, already carrying `readonly` and the plan schedule
 * context) and the renderer prefers it over this token. The token stays the seam that tests and
 * non-board hosts override, and the fallback when a mount passes no kinds.
 */
export const OUTLINE_ENTITY_KINDS = new InjectionToken<OutlineEntityKinds>('OUTLINE_ENTITY_KINDS');

/** Board `params` key carrying the mount's entity pair. Read with `resolveBoardParams`. */
export interface OutlineEntityKindsParams {
  outlineEntityKinds?: OutlineEntityKinds;
  /** Refreshes the builder's one-fetch tree cache before a newly created row is revealed. */
  reloadOutline?: () => Promise<OutlineRow[]>;
  /** Optional live-plan capability; absent on template and Visit outlines. */
  addTasksFromChecklist?: (parent: EntityLink, onSaved?: (taskIds: string[]) => void | Promise<void>) => void;
  /** Mount opted into the line "No crew" chip; see `OutlineRelationFeatures.noCrew`. */
  flagNoCrew?: boolean;
}
